import { faRightLeft } from '@fortawesome/pro-regular-svg-icons'
import {
  faCalendarDays,
  faClockRotateLeft,
  faExclamationTriangle,
  faGear,
  faInboxFull,
  faMagicWandSparkles
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { usePathViewedActivity } from '~/api/queries/pathViewed'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { NotificationsViewer } from '~/components/Layout/Header/Notification/NotificationViewer'
import { useConfig } from '~/domains/configuration/queries/config'
import { TasksViewer } from '~/domains/tasks/components/TasksViewer/TasksViewer'
import { logoPicture40 } from '~/styles/picture.css'
import { themeClass } from '~/styles/theme.css'
import { buildV3Url } from '~/utils/legacy'
import { GlobalSearch } from '../../Scopes/Search/GlobalSearch'
import { Menu, type MenuProps } from '../../Scopes/Users/Menu'
import {
  banner,
  brandName,
  brandSection,
  sandboxBadge,
  searchBlock,
  toolBar,
  toolBarDot,
  toolBaritem,
  toolBaritemActive,
  toolBarTick
} from './Header.css'
import { UserSwitcher } from './UserSwitcher'

type HeaderProps = {
  brand?: {
    logo: string | null
    name: string
  }
  events?: number
  resumes?: number
  superUser?: boolean
  currentSection?: string
  legacyUrl?: string | null
  displaySearch?: boolean
  legacySwap?: { launch: () => void }
} & MenuProps

export const Header = ({
  brand,
  superUser = false,
  events,
  resumes,
  user,
  currentSection = '',
  legacyUrl = '',
  displaySearch = true,
  legacySwap
}: HeaderProps): ReactElement => {
  const { data: isSandboxEnabled } = useConfig('sandbox.enabled')
  const { hasNewActivity: hasNewMessagingActivity } =
    usePathViewedActivity('/messaging')
  const messagingItemClassName = clsx(
    toolBaritem,
    currentSection?.startsWith('/messaging') ? toolBaritemActive : null
  )
  const messagingItemContent = (
    <>
      <FontAwesomeIcon icon={faInboxFull} />
      {hasNewMessagingActivity && <span className={toolBarDot} />}
    </>
  )
  return (
    <div className={themeClass}>
      <header className={banner}>
        {brand !== undefined && window.JobConfig !== undefined && (
          <a href={buildV3Url('/dashboard')} className={brandSection}>
            {brand.logo !== null && (
              <img
                src={brand.logo}
                alt={brand.name}
                className={logoPicture40}
              />
            )}
            <span className={brandName}>{brand.name}</span>
          </a>
        )}
        {brand !== undefined && window.JobConfig === undefined && (
          <Link to="/dashboard" className={brandSection}>
            {brand.logo !== null && (
              <img
                src={brand.logo}
                alt={brand.name}
                className={logoPicture40}
              />
            )}
            <span className={brandName}>{brand.name}</span>
          </Link>
        )}
        {brand === undefined && <Shimmer height={40} width={40} />}
        {isSandboxEnabled && (
          <Tooltip
            direction="bottom"
            text="Les mails, SMS, etc. ne seront pas envoyés. Le mode test est destiné uniquement à la formation et aux tests."
            asChild
          >
            <span className={sandboxBadge}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Mode test
            </span>
          </Tooltip>
        )}
        {displaySearch && (
          <div className={searchBlock}>
            {brand !== undefined && <GlobalSearch />}
            {brand === undefined && <Shimmer height={40} width={300} />}
          </div>
        )}

        {brand !== undefined && user !== undefined && (
          <div className={toolBar}>
            {superUser && (
              <Tooltip
                direction="bottom"
                text="Configuration superadmin"
                as="span"
              >
                <a
                  href={`${legacyUrl}/admin/securite/superadmin`}
                  className={toolBaritem}
                  aria-label="Configuration superadmin"
                >
                  <FontAwesomeIcon icon={faGear} />
                </a>
              </Tooltip>
            )}
            {superUser && legacySwap !== undefined && (
              <Tooltip
                direction="bottom"
                text="Changer d'utilisateur"
                as="span"
              >
                <span
                  className={toolBaritem}
                  onClick={() => legacySwap?.launch()}
                >
                  <FontAwesomeIcon icon={faRightLeft} />
                </span>
              </Tooltip>
            )}
            {legacySwap === undefined && (
              <Tooltip direction="bottom" text="Changer d'utilisateur">
                <UserSwitcher classname={toolBaritem}>
                  <FontAwesomeIcon icon={faRightLeft} />
                </UserSwitcher>
              </Tooltip>
            )}
            {window.JobConfig !== undefined && (
              <Tooltip direction="bottom" text="Assistant" as="span">
                <a
                  href={buildV3Url('/assistant')}
                  title="Assistant"
                  className={clsx(
                    toolBaritem,
                    currentSection === 'assistant' ? toolBaritemActive : null
                  )}
                >
                  <FontAwesomeIcon icon={faMagicWandSparkles} />
                </a>
              </Tooltip>
            )}
            {window.JobConfig === undefined && (
              <Tooltip direction="bottom" text="Assistant" as="span">
                <Link
                  to="/assistant"
                  title="Assistant"
                  className={toolBaritem}
                  activeProps={{
                    className: clsx(toolBaritem, toolBaritemActive)
                  }}
                >
                  <FontAwesomeIcon icon={faMagicWandSparkles} />
                </Link>
              </Tooltip>
            )}
            {resumes !== undefined && resumes > 0 && (
              <Tooltip direction="bottom" text="CV à traiter" as="span">
                <a
                  href={`${legacyUrl}/admin/candidats/upload`}
                  className={toolBaritem}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span className={toolBarTick}>{resumes}</span>
                </a>
              </Tooltip>
            )}
            <Tooltip direction="bottom" text="Messagerie" as="span">
              {window.JobConfig !== undefined ? (
                <a
                  href={buildV3Url('/messaging')}
                  title="Messagerie"
                  className={messagingItemClassName}
                >
                  {messagingItemContent}
                </a>
              ) : (
                <Link
                  to="/messaging"
                  title="Messagerie"
                  className={messagingItemClassName}
                >
                  {messagingItemContent}
                </Link>
              )}
            </Tooltip>
            <TasksViewer classname={toolBaritem} currentUserId={user.id} />
            <Tooltip direction="bottom" text="Activité (historique)" as="span">
              <a
                title="Activité (historique)"
                href={`${legacyUrl}/admin/accueil/tasks`}
                className={clsx(
                  toolBaritem,
                  currentSection === 'tasks' ? toolBaritemActive : null
                )}
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </a>
            </Tooltip>
            <Tooltip direction="bottom" text="Agenda" as="span">
              <a
                title="Agenda"
                href={`${legacyUrl}/admin/agenda`}
                className={clsx(
                  toolBaritem,
                  currentSection === 'calendar' ? toolBaritemActive : null
                )}
              >
                <FontAwesomeIcon icon={faCalendarDays} />
                {events !== undefined && events > 0 && (
                  <span className={toolBarTick}>{events}</span>
                )}
              </a>
            </Tooltip>
            <NotificationsViewer
              classname={toolBaritem}
              defaultCount={user.notificationCount}
              currentUserId={user.id}
            />
          </div>
        )}

        {brand === undefined && (
          <div className={toolBar}>
            <Shimmer height={40} width={200} />
          </div>
        )}
        <Menu user={user} legacyUrl={legacyUrl} />
      </header>
    </div>
  )
}
